// Werksfilter (Lager-WerkDropdown/PM-PlantSelect-Idiom) MIT "Alle Werke"-Option.
// "Alle Werke" (value '') = kein Filter -> das Backend scopet ohnehin auf die dem
// User erlaubten Werke. Einzelauswahl -> genau dieses Werk. Optionen kommen aus
// GET /einvoicing/plants (nur die dem User sichtbaren Werke).

import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Factory } from 'lucide-react';

/**
 * @param {Array<{plant,name}>} plants  dem User sichtbare Werke
 * @param {string} value                aktuell gewaehltes Werk ('' = Alle Werke)
 * @param {(plant:string)=>void} onChange
 */
const PlantFilter = ({ plants = [], value = '', onChange, isDarkMode, t }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Nur ein einziges Werk und keine Wahl noetig? Trotzdem anzeigen (Kontext), aber statisch.
  const current = value ? (plants.find((p) => p.plant === value)) : null;
  const label = current ? `${current.plant} · ${current.name}` : t('einvoicing.allPlants');

  const btn = isDarkMode
    ? 'bg-white/[0.08] border-white/15 text-white/80 hover:bg-white/[0.12]'
    : 'bg-white border-slate-200 text-slate-700 hover:border-violet-400/40';
  const menu = isDarkMode
    ? 'bg-gray-900 border-white/15 shadow-[0_8px_32px_rgba(0,0,0,0.5)]'
    : 'bg-white border-slate-200';

  const item = (active) => `w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-3 ${active
    ? (isDarkMode ? 'bg-violet-500/20 text-violet-200' : 'bg-violet-50 text-violet-700')
    : (isDarkMode ? 'text-white/80 hover:bg-white/[0.08]' : 'text-slate-700 hover:bg-slate-50')}`;

  const options = [{ plant: '', name: t('einvoicing.allPlants') }, ...plants];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium border transition-all ${btn}`}
      >
        <Factory className={`w-4 h-4 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
        <span className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('einvoicing.plant')}:</span>
        <span className="truncate max-w-[160px]">{label}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''} ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
      </button>

      {open && (
        <div className={`absolute left-0 top-full mt-1 z-40 min-w-[220px] max-w-[calc(100vw-2rem)] rounded-xl border overflow-hidden ${menu}`}>
          {options.map((p) => (
            <button
              key={p.plant || '__all__'}
              type="button"
              onClick={() => { setOpen(false); onChange(p.plant); }}
              className={item(p.plant === value)}
            >
              {p.plant
                ? <><span className={`font-mono text-xs flex-shrink-0 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{p.plant}</span><span className="truncate min-w-0">{p.name}</span></>
                : <span className="font-medium">{t('einvoicing.allPlants')}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlantFilter;
